<div class="case-record">
  <section class="search-card">
    <h2 class="section-title">
      {{ 'app.patrol.discord.cockpit.caseRecord.title' | translate }}
    </h2>
    <p class="section-hint">
      {{ 'app.patrol.discord.cockpit.caseRecord.hint' | translate }}
    </p>

    <form class="search-form" (ngSubmit)="search()">
      <label class="search-field">
        <span class="search-field__label">
          {{ 'app.patrol.discord.cockpit.caseRecord.caseIdLabel' | translate }}
        </span>
        <input
          type="text"
          class="search-field__input"
          [ngModel]="caseIdQuery()"
          (ngModelChange)="caseIdQuery.set($event)"
          name="caseId"
          autocomplete="off"
          [placeholder]="
            'app.patrol.discord.cockpit.caseRecord.caseIdPlaceholder' | translate
          "
        />
      </label>

      <label class="search-field">
        <span class="search-field__label">
          {{
            'app.patrol.discord.cockpit.caseRecord.discordUserLabel' | translate
          }}
        </span>
        <input
          type="text"
          class="search-field__input"
          [ngModel]="discordUserQuery()"
          (ngModelChange)="discordUserQuery.set($event)"
          name="discordUserId"
          autocomplete="off"
          [placeholder]="
            'app.patrol.discord.cockpit.caseRecord.discordUserPlaceholder'
              | translate
          "
        />
      </label>

      <button type="submit" class="search-form__btn" [disabled]="isLoading()">
        {{ 'app.patrol.discord.cockpit.caseRecord.search' | translate }}
      </button>
    </form>
  </section>

  @if (isLoading()) {
    <p class="case-record__hint">
      {{ 'app.patrol.discord.cockpit.caseRecord.loading' | translate }}
    </p>
  } @else if (loadError() === 'search_failed') {
    <p class="case-record__error" role="alert">
      {{ 'app.patrol.discord.cockpit.caseRecord.searchError' | translate }}
    </p>
  } @else if (loadError() === 'load_failed') {
    <p class="case-record__error" role="alert">
      {{ 'app.patrol.discord.cockpit.caseRecord.loadError' | translate }}
    </p>
  }

  @if (!isLoading() && caseSummaries().length > 1) {
    <section class="results-card">
      <h3 class="subsection-title">
        {{ 'app.patrol.discord.cockpit.caseRecord.results' | translate }}
      </h3>
      <div class="table-wrap">
        <table class="results-table">
          <thead>
            <tr>
              <th>
                {{ 'app.patrol.discord.cockpit.caseRecord.colCase' | translate }}
              </th>
              <th>
                {{ 'app.patrol.discord.cockpit.caseRecord.colTime' | translate }}
              </th>
              <th>
                {{
                  'app.patrol.discord.cockpit.caseRecord.colMember' | translate
                }}
              </th>
              <th>
                {{
                  'app.patrol.discord.cockpit.caseRecord.colViolation'
                    | translate
                }}
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            @for (item of caseSummaries(); track item.caseId) {
              <tr>
                <td><code>{{ shortCaseId(item.caseId) }}</code></td>
                <td>{{ formatDate(item.occurredAt) }}</td>
                <td>
                  @if (item.platformUserId) {
                    <code>{{ item.platformUserId }}</code>
                  } @else {
                    —
                  }
                </td>
                <td>{{ item.violationType ?? '—' }}</td>
                <td>
                  <button
                    type="button"
                    class="link-btn"
                    (click)="openCase(item.caseId)"
                  >
                    {{
                      'app.patrol.discord.cockpit.caseRecord.view' | translate
                    }}
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>
  }

  @if (caseRecord(); as record) {
    <section class="detail-card">
      <div class="detail-card__header">
        <div>
          <h3 class="subsection-title">
            {{
              'app.patrol.discord.cockpit.caseRecord.detailTitle'
                | translate: { id: shortCaseId(record.caseId) }
            }}
          </h3>
          <p class="detail-meta">
            {{ formatDate(record.startedAt) }}
            @if (record.channelName) {
              · {{ record.channelName }}
            }
          </p>
        </div>
        <button type="button" class="export-btn" (click)="copyExport()">
          @if (copiedExport()) {
            {{ 'app.patrol.discord.cockpit.caseRecord.copied' | translate }}
          } @else {
            {{ 'app.patrol.discord.cockpit.caseRecord.copyExport' | translate }}
          }
        </button>
      </div>

      <dl class="detail-grid">
        <div>
          <dt>{{ 'app.patrol.discord.cockpit.caseRecord.member' | translate }}</dt>
          <dd>
            @if (record.platformUserId) {
              <code>{{ record.platformUserId }}</code>
            } @else {
              —
            }
          </dd>
        </div>
        <div>
          <dt>
            {{ 'app.patrol.discord.cockpit.caseRecord.valvurLinked' | translate }}
          </dt>
          <dd>
            {{
              (record.linkedOnValvur
                ? 'app.patrol.discord.cockpit.caseRecord.yes'
                : 'app.patrol.discord.cockpit.caseRecord.no'
              ) | translate
            }}
          </dd>
        </div>
        <div>
          <dt>
            {{ 'app.patrol.discord.cockpit.caseRecord.violation' | translate }}
          </dt>
          <dd>
            @if (record.violationType) {
              {{ record.violationType }} ({{ record.severity ?? '—' }})
            } @else {
              —
            }
          </dd>
        </div>
        <div>
          <dt>
            {{ 'app.patrol.discord.cockpit.caseRecord.reputation' | translate }}
          </dt>
          <dd>
            @if (record.reputationDelta != null) {
              {{ record.previousReputation ?? '?' }} →
              {{ record.newReputation ?? '?' }}
              ({{ record.reputationDelta }})
            } @else {
              —
            }
          </dd>
        </div>
        <div>
          <dt>
            {{ 'app.patrol.discord.cockpit.caseRecord.harmRecorded' | translate }}
          </dt>
          <dd>
            {{
              (record.harmAsOffenderCount > 0
                ? 'app.patrol.discord.cockpit.caseRecord.yes'
                : 'app.patrol.discord.cockpit.caseRecord.no'
              ) | translate
            }}
          </dd>
        </div>
        @if (record.modQueue) {
          <div>
            <dt>
              {{ 'app.patrol.discord.cockpit.caseRecord.modQueue' | translate }}
            </dt>
            <dd>
              {{ record.modQueue.proposedAction }} · tier
              {{ record.modQueue.tier }} · {{ record.modQueue.status }}
            </dd>
          </div>
        }
      </dl>

      <h4 class="events-title">
        {{ 'app.patrol.discord.cockpit.caseRecord.timeline' | translate }}
      </h4>
      <div class="table-wrap">
        <table class="events-table">
          <thead>
            <tr>
              <th>
                {{ 'app.patrol.discord.cockpit.caseRecord.colTime' | translate }}
              </th>
              <th>
                {{
                  'app.patrol.discord.cockpit.caseRecord.colAction' | translate
                }}
              </th>
              <th>
                {{
                  'app.patrol.discord.cockpit.caseRecord.colReason' | translate
                }}
              </th>
            </tr>
          </thead>
          <tbody>
            @for (event of record.events; track event.occurredAt + event.action) {
              <tr>
                <td>{{ formatDate(event.occurredAt) }}</td>
                <td>
                  <span class="action-badge" [attr.data-action]="event.action">
                    {{ actionLabel(event.action) | translate }}
                  </span>
                </td>
                <td>{{ event.reason ?? '—' }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <p class="privacy-note">
        {{ 'app.patrol.discord.cockpit.caseRecord.privacyNote' | translate }}
      </p>
    </section>
  } @else if (
    !isLoading() &&
    !loadError() &&
    caseSummaries().length === 0 &&
    (caseIdQuery() || discordUserQuery())
  ) {
    <p class="empty-hint">
      {{ 'app.patrol.discord.cockpit.caseRecord.empty' | translate }}
    </p>
  }
</div>
